import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert } from 'lucide-react';
import CeoDashboard from './reports/CeoDashboard';
import MetricExplorer from './reports/MetricExplorer';
import NeedsAttention from './reports/NeedsAttention';
import CeoOverview from './reports/CeoOverview';
import DrilldownModal from './reports/DrilldownModal';
import AiPipelineReport from './reports/AiPipelineReport';
import LastReportChip from './reports/LastReportChip';
import { subscribeJob } from '../lib/ceoReportJob';
import { RevenueChart, MovementWaterfall, SalesFunnel } from './reports/CeoCharts';
import DownloadMenu from './reports/DownloadMenu';
import { mergeRepRows, overdueRowsFromAttention } from './reports/_overviewData';
import { presetRequest } from './reports/_ceoMetrics';
import { customRange } from '../lib/easyReports';
import { readParam, writeParams } from '../lib/urlState';
import FinancePanel from './reports/FinancePanel';
import FinanceDrilldown, { FINANCE_CHANNELS } from './reports/FinanceDrilldown';
import RepReport, { REP_CHANNELS } from './reports/RepReport';
import ContactDetailView from './crm/ContactDetailView';
import { targetFor } from './reports/_attentionTargets';
import './reports/ceoSoftUi.css';
import { metricTitle } from './reports/_metricTitles';

/*
 * CEO Dashboard page (Carlos §4.7, management-only). Thin container: enforces the
 * reports:management:read gate client-side (the Sidebar already hides the nav
 * entry without it; this is the belt-and-suspenders no-silent-403 guard so a
 * direct navigation never fires an IPC call that would 403), then loads the
 * aggregate once via reports:ceo:run and hands it to the presentational
 * CeoDashboard. One load per mount + an explicit Refresh button — no polling.
 * Server-side secureHandler("reports:management:read") is the authoritative gate.
 */

const MGMT_PERM = 'reports:management:read';
// Card F1. Client-side gate for whether tiles are OFFERED as clickable — the
// authoritative gate is secureHandler("analytics:drilldown") on the channel plus
// the resolver's own per-metric check. This only avoids inviting a press that the
// server would refuse (the same reasoning as the referral queue's Promote button).
const DRILLDOWN_PERM = 'analytics:drilldown';
/*
 * P140 PR B. Whether the Export CSV button is OFFERED. The authoritative gate is
 * secureHandler("analytics:drilldown:export") on the channel PLUS the resolver's
 * own re-check — this only avoids inviting a press the server would refuse.
 *
 * ⚠ analytics:drilldown:export is ADMIN ONLY. The executive role holds
 * analytics:drilldown (read the rows on screen) and is DELIBERATELY excluded
 * from export — rbac.js:636 records the ruling: reading rows and extracting a
 * file are different risks. So the CEO sees the summary and the 25-row list and
 * does NOT get the CSV button. That is the existing ruling, not a new decision
 * taken here; widening it is a one-line grant and needs Daniel's word.
 */
const EXPORT_PERM = 'analytics:drilldown:export';
/*
 * P142 PR B. Whether the finance panel is REQUESTED at all. The authoritative
 * gate is secureHandler("finance:read") on the channel; this avoids firing an
 * IPC call the server would refuse, and — more importantly — avoids rendering an
 * empty finance panel to someone without the grant, which would read as "there
 * is no money" rather than "this is not yours to see".
 *
 * ⚠ finance:read is RESTRICTED and held by NO role. It reaches a person only by
 * explicit per-user grant.
 */
const FINANCE_PERM = 'finance:read';

/*
 * Card P254 — WRITING the monthly revenue target. Held by sysadmin and admin
 * only (database/rbac.js).
 *
 * ⚠ DELIBERATELY NOT MGMT_PERM. Reading the goal tracker is a dashboard
 * grant; setting the number the whole screen is measured against is a
 * settings change, and reports.js already records why those must not be the
 * same gate — a target accepted on the report channel would let any holder of
 * the dashboard grant draw a goal the business never agreed to. The server is
 * the authority on this; the constant here only decides whether the control
 * is drawn, and a user without it never sees a button that would 403.
 */
const SETTINGS_PERM = 'settings:update';

/*
 * Titles for the finance drill-downs. Code-supplied so a metric key never
 * reaches the screen as a heading — and so an unknown key renders no panel at
 * all rather than one headed `finance.whatever`.
 */
/*
 * ⛔⛔ A RAW METRIC KEY MUST NEVER REACH A HEADING. The Overview's drill-down
 * opened a panel titled "ceo.open_pipeline_value" — a storage key on the CEO's
 * screen, which is the same class as `d90_plus` in P154.
 *
 * ⚠ `metricTitle()` is the ONLY way a title is produced, and it REFUSES a key
 * it does not know rather than falling back to the key itself. A fallback is
 * what put the key on screen in the first place: it renders, it looks like a
 * label to the code, and only a human reading it can tell.
 */
// ⭐ The titles live in _metricTitles.js so a NON-JSX unit test can join them
// against the server registries. See P160 item 5.
export { METRIC_TITLES, metricTitle } from './reports/_metricTitles';

export default function CeoDashboardPage({ user, onNavigate, initialView = 'overview', sessionNonce = ''}) {
  const canView = Array.isArray(user?.permissions) && user.permissions.includes(MGMT_PERM);
  const canDrill = Array.isArray(user?.permissions) && user.permissions.includes(DRILLDOWN_PERM);
  const canExport = Array.isArray(user?.permissions) && user.permissions.includes(EXPORT_PERM);
  const canFinance = Array.isArray(user?.permissions) && user.permissions.includes(FINANCE_PERM);
  const canSetTarget = Array.isArray(user?.permissions) && user.permissions.includes(SETTINGS_PERM);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /*
   * ⛔⛔ ONE STACK, NOT FOUR STATES — Card P167.
   *
   * There were four independent holders: `drilldown` (a modal, from #742)
   * and `financeMetric` / `repOpen` / `exception` (inline panels that
   * scrolled into view, from #745). Four states meant two experiences and
   * no way to navigate between levels — opening rows from a group view
   * replaced the group rather than stacking on it, so Back did not exist.
   *
   * ⚠ Each frame carries its own TITLE, so the breadcrumb is built from the
   * stack rather than recomputed — a title derived twice is a title that can
   * disagree with itself.
   */
  const [repOverdue, setRepOverdue] = useState(null);
  const [stack, setStack] = useState([]);
  const pushFrame = useCallback((frame) => {
    if (!frame || !frame.kind) return;
    setStack((s0) => [...s0, frame]);
  }, []);
  const popFrame = useCallback(() => setStack((s0) => s0.slice(0, -1)), []);
  const closeStack = useCallback(() => setStack([]), []);

  /*
   * ⚠ Shims so every existing call site keeps working unchanged. A sweeping
   * rename across ten call sites in the same PR as a behaviour change is how
   * one of them gets missed silently.
   */
  const setDrilldown = useCallback((d) => {
    if (!d) { closeStack(); return; }
    pushFrame({ kind: 'metric', key: d.key, title: d.title || d.key });
  }, [pushFrame, closeStack]);
  const setFinanceMetric = useCallback((key) => {
    if (!key) { closeStack(); return; }
    pushFrame({ kind: 'finance', key, title: metricTitle(key) || 'Finance' });
  }, [pushFrame, closeStack]);
  /*
   * ⭐ P229 — is a report being written right now? Read from the P219 job
   * store, so this page holds no duplicate state about it.
   */
  const [aiJobRunning, setAiJobRunning] = useState(false);
  useEffect(() => subscribeJob((j) => setAiJobRunning(j.status === 'running')), []);

  /* ⭐ P229 — one opener, used by the header button and by the chip. */
  const openAiReport = useCallback(() => {
    pushFrame({ kind: 'ai', title: 'AI pipeline report' });
  }, [pushFrame]);

  const setRepOpen = useCallback((rep) => {
    if (!rep) { closeStack(); return; }
    pushFrame({ kind: 'rep', rep, title: rep.label || rep.rep || 'Rep report' });
  }, [pushFrame, closeStack]);
  const setException = useCallback((ex) => {
    if (!ex) { closeStack(); return; }
    /*
     * ⛔⛔ THE CHANNEL SET FOLLOWS THE REGISTRY THE METRIC LIVES IN. `channels`
     * was hardcoded to 'rep', which was right while every exception target was
     * a rep metric and silently wrong the moment one was not: `finance.unpaid`
     * asked over the rep channels would come back UNKNOWN_METRIC, and the
     * pop-up would open empty rather than refusing — a dead panel that looks
     * like "no records" instead of a wiring bug.
     */
    const channels = String(ex.key || '').startsWith('finance.') ? 'finance' : 'rep';
    pushFrame({ kind: 'metric', key: ex.key, title: ex.title || ex.key,
                columns: ex.columns, group: ex.group, dimension: ex.dimension || null,
                filters: ex.filters, channels });
  }, [pushFrame, closeStack]);

  const drilldown = null;
  /*
   * P140 PR A. The attention panel loads SEPARATELY from the aggregate, and that
   * is deliberate: folding it into reports:ceo:run would mean one slow or broken
   * rule takes the whole dashboard down with it, and — worse — a single `data`
   * object cannot express "the tiles loaded but the checks did not". Two loads
   * give the panel its own failed state, which is what keeps a failure from
   * rendering as an empty, calm panel (#686).
   */
  /*
   * ⛔ OVERVIEW IS THE DEFAULT, AND THE OLD SCREEN IS STILL REACHABLE.
   *
   * The detail dashboard is the only place several figures exist (lead
   * sources, interactions, the not-yet-tracked inventory). Replacing it
   * outright would delete working reporting to ship a nicer first screen;
   * a toggle costs one line and keeps every number someone already relies on.
   */
  const [view, setView] = useState(initialView);
  const [reps, setReps] = useState(null);
  const [stages, setStages] = useState(null);
  const overviewRef = useRef(null);

  /*
   * ⛔ The export reads the SAME values the screen is rendering — one data
   * source, no second query. A refetch for the download is how a PDF comes
   * to disagree with the dashboard it was printed from.
   */


  const [stagesFailed, setStagesFailed] = useState(false);
  const [repsFailed, setRepsFailed] = useState(false);
  const [period, setPeriod] = useState('Month');
  /*
   * ⭐⭐ P185 — THE CUSTOM WINDOW, HELD BESIDE THE PRESET AND NOT INSTEAD OF IT.
   *
   * `period` stays a DISPLAY STRING because that is what it already is: W1's
   * AI report renders it ("Ask about the pipeline — {period}") and so does the
   * export model. Making it an object would have broken both silently, with a
   * heading reading "[object Object]".
   *
   * ⚠ ONE DERIVED REQUEST, built below. Both shapes — {preset} and {from,to} —
   * were ALREADY understood by routes/_ceo_dashboard.js (resolveWindow) and
   * routes/_finance_summary.js (resolvePeriod). The custom range therefore
   * reuses the period plumbing rather than opening a second path to the same
   * numbers, which is how a dashboard ends up with two answers to one question.
   */
  const [custom, setCustom] = useState(() => {
    // Restored from the URL on first render, so a reload and the browser Back
    // button both land on the window the user chose. Validated, never trusted.
    const f = readParam('from');
    const t = readParam('to');
    if (!f || !t) return null;
    const r = customRange(f, t);
    return r.ok ? { from: r.from.toISOString(), to: r.to.toISOString(), label: r.label, fromYmd: f, toYmd: t } : null;
  });

  /** The window label a human reads — a preset name, or the range in words. */
  const periodLabel = custom ? custom.label : period;

  /*
   * ⚠ The request envelope. A custom window sends {from,to}; a preset sends
   * {preset}. Built ONCE here so every loader below asks the same question.
   */
  const periodRequest = React.useMemo(
    () => (custom ? { from: custom.from, to: custom.to } : presetRequest(period)),
    [custom, period]);

  /*
   * ⭐ P185 item 4 — THE ONE GETTER W1 ASKED FOR. The AI report takes its
   * period from this, so a custom range flows into the report without P182
   * re-implementing the parse, the validation or the business-time boundary.
   * A second implementation would be a second answer.
   */
  const currentRange = React.useMemo(() => ({
    kind: custom ? 'custom' : 'preset',
    label: periodLabel,
    preset: custom ? null : period,
    from: custom ? custom.from : null,
    to: custom ? custom.to : null,
  }), [custom, period, periodLabel]);

  /** Apply or clear the custom window, mirroring it into the URL. */
  const applyCustom = useCallback((next) => {
    setCustom(next);
    writeParams({ from: next ? next.fromYmd : '', to: next ? next.toYmd : '' });
  }, []);
  const [attention, setAttention] = useState(null);
  const [attentionFailed, setAttentionFailed] = useState(false);
  const [attentionLoading, setAttentionLoading] = useState(false);

  /*
   * ⛔ ONE merge, shared by the screen and the export — the card's "same
   * numbers as the screen" rule. A second mergeRepRows call in the export
   * path would be a second definition of a rep's overdue count.
   *
   * ⚠ Declared AFTER the state it reads: the first placement sat above
   * `attention` and threw "Cannot access 'attention' before initialization"
   * at RUNTIME while the production build compiled cleanly. A green build is
   * not an executed one.
   */
  const mergedRepsForExport = React.useMemo(
    () => mergeRepRows({ performance: reps || [], overdue: overdueRowsFromAttention(attention) }),
    [reps, attention]
  );
  /*
   * Loaded SEPARATELY again, for the same reason the attention panel is: one
   * slow or broken source must not take the whole dashboard down, and a single
   * `data` object cannot express "the tiles loaded but finance did not".
   */
  const [finance, setFinance] = useState(null);
  const [financeFailed, setFinanceFailed] = useState(false);
  const [financeLoading, setFinanceLoading] = useState(false);

  /*
   * P204 — the affiliate programme. ⚠ ITS OWN CHANNEL, not folded into
   * reports:ceo:run: a slow or broken affiliate query must not take the
   * headline tiles down with it, and the two grants are not the same shape.
   */
  const [affiliate, setAffiliate] = useState(null);
  const [affiliateFailed, setAffiliateFailed] = useState(false);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  // P150. Which Revenue figure is open, if any. Held here rather than in the
  // panel so the drill-down renders beside the card, like #722's does.

  // P160 item 1 — which rep's own report is open, if any.

  // P160 item 2 — an exception opened as the records it counts.

  /*
   * ⛔⛔ P160 item 6. The panel opens at the TOP of the page while the button
   * that opened it is far below, so to Carlos the click looked like it did
   * nothing — "the button clicks but no report". A panel that renders off
   * screen is indistinguishable from one that never rendered.
   *
   * ⚠ Scrolled AND focused: scrolling moves the eye, focus moves the keyboard
   * and tells a screen reader something happened. Doing only the first leaves
   * anyone not using a mouse exactly where they were.
   */
  /*
   * ⚠ The period, as the drill-down filters take it. The rep report must answer
   * for the SAME window the Overview is showing, or "deals won" beside a
   * quarter switch would silently mean all time.
   */
  /*
   * P196 — the Overview graphics. ⭐ ONE payload for all three charts, taking
   * the SAME period filters as the tiles, so a chart cannot show a different
   * window from the number above it.
   */
  const [charts, setCharts] = React.useState(null);
  const [chartsState, setChartsState] = React.useState('loading');
  /* Card P254 — the goal tracker. Its own payload and its own state, because
     it is the one block on this page that does NOT move with the period
     switch: it is always the current business month. */
  const [goal, setGoal] = React.useState(null);
  const [goalState, setGoalState] = React.useState('loading');
  const [goalNonce, setGoalNonce] = React.useState(0);
  const [chartPipeline, setChartPipeline] = React.useState('');

  /*
   * ⛔⛔ THE DASHBOARD'S OWN PERIOD — NEVER THE FINANCE RESPONSE (Card P217,
   * from W3's P214 finding).
   *
   * This read `finance.range`, so every consumer below — the metric summary,
   * the stage drill-downs, getCeoCharts — silently fell back to the SERVER'S
   * DEFAULT WINDOW in three ordinary situations:
   *
   *   · the user has no finance:read grant, so the finance call never runs;
   *   · the finance call failed;
   *   · it simply had not resolved yet.
   *
   * ⚠ WHY THAT IS WORSE THAN AN ERROR: the drill-down still opens and still
   * shows rows. Nothing on screen says the window is not the one the period
   * pills show, so a figure for "this quarter" is read as this quarter while
   * being something else entirely.
   *
   * ⭐ A CUSTOM RANGE NEEDS NO RESOLUTION — the user typed the dates, so they
   * are used verbatim. A PRESET is resolved by the SERVER, off the dashboard's
   * own response to `periodRequest`: resolveWindow() in routes/_ceo_dashboard.js
   * via _date_presets, the same code the tiles were computed with. Resolving a
   * preset here instead would be a second implementation of "this quarter",
   * and the client would win — which is exactly how two numbers on one screen
   * come to disagree on a boundary day.
   *
   * ⚠ Null still means ALL TIME (an unbounded preset has no from), which is
   * correct and is what every consumer already expects.
   */
  const periodFilters = React.useMemo(() => {
    if (custom && custom.from) return { from: custom.from, to: custom.to || null };
    const r = data && data.range;
    return r && r.from ? { from: r.from, to: r.to } : null;
  }, [custom, data]);

  /*
   * ⛔⛔ CARD P214 ITEM 4 — THE AI REPORT ASKS FOR THE DASHBOARD'S WINDOW.
   *
   * It used to be handed `periodFilters`, which is the window the FINANCE
   * SUMMARY RESPONSE came back with. In the happy path that is the same
   * window, since loadFinance() is given the same periodRequest. It diverges
   * silently in three ordinary situations, and in each one the brief
   * describes a DIFFERENT period from the tiles beside it while looking
   * perfectly normal:
   *
   *   · the user lacks FINANCE_PERM, so loadFinance() returns early and
   *     `finance` stays null -> filters null -> the server falls back to its
   *     DEFAULT window;
   *   · the finance call failed, which deliberately sets finance to null;
   *   · the report is generated before finance has resolved.
   *
   * ⚠ It also mislabelled the happy path: a resolved {from,to} makes
   * buildCeoDashboard call the window "Custom range" whatever the user
   * picked, which is why the dialog said "Month" and the brief said "Custom
   * range" on v2.1.54.
   *
   * ⛔⛔ A SEPARATE CONSTANT, NOT A REDEFINITION OF periodFilters. I changed
   * periodFilters itself first and it broke two drill-down tests: SEVEN call
   * sites read it — the metric summary, the stage drill-downs, getCeoCharts —
   * and they were built against a resolved {from,to}, not against {preset}.
   * Fixing the report's window is this card; re-pointing the whole dashboard
   * is not, and doing it by accident inside a polish PR is how a cosmetic
   * change acquires a blast radius. ⚠ The same finance-null hazard applies to
   * those call sites and is REPORTED rather than silently fixed here.
   */
  const aiReportFilters = periodRequest;

  /*
   * ⛔ THE SCROLL-INTO-VIEW ANCHOR IS GONE — Card P167.
   *
   * #745 rendered its panels inline and scrolled the page to them. Every
   * drill-down is now a dialog in a fixed position, so there is nothing to
   * scroll to and no ref, effect or anchor div left behind. Moving the page
   * to reveal an answer made that answer's location depend on where the
   * question was asked; a dialog is in the same place every time.
   */

  /*
   * ⚠⚠ DECLARED AFTER EVERY PIECE OF STATE THEY READ. Both of these were
   * first written beside the ref at the top of the component and threw
   * "Cannot access 'attention'/'finance' before initialization" at RUNTIME
   * while `react-scripts build` reported "Compiled successfully". A const in
   * the temporal dead zone is valid syntax; only executing it finds it.
   */
  const getModelArgs = useCallback(() => ({
    data, finance: canFinance ? finance : null, attention,
    reps: mergedRepsForExport, stages, period: periodLabel, asOf: (data && data.asOf) || null,
  }), [data, finance, canFinance, attention, mergedRepsForExport, stages, periodLabel]);

  const auditExport = useCallback((info) => {
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (api && typeof api.auditCeoExport === 'function') api.auditCeoExport(info);
    } catch (e) { /* the file is already saved; a failed log must not undo it */ }
  }, []);


  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.runCeoDashboard !== 'function') {
        throw new Error('CEO dashboard is unavailable in this context.');
      }
      const res = await api.runCeoDashboard(periodRequest);
      if (res && res.error) throw new Error(res.message || 'Failed to load the CEO dashboard.');
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load the CEO dashboard.');
    } finally {
      setLoading(false);
    }
  }, [canView, periodRequest]);

  const loadAttention = useCallback(async () => {
    if (!canView) return;
    setAttentionLoading(true);
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.runCeoAttention !== 'function') {
        throw new Error('unavailable');
      }
      const res = await api.runCeoAttention();
      if (res && res.error) throw new Error(res.message || 'failed');
      setAttention(res);
      setAttentionFailed(false);
    } catch (e) {
      // ⚠ The panel is told it FAILED. It is never left holding stale or null
      // data that it would render as "nothing needs attention".
      setAttention(null);
      setAttentionFailed(true);
    } finally {
      setAttentionLoading(false);
    }
  }, [canView]);

  const loadFinance = useCallback(async () => {
    if (!canView || !canFinance) return;
    setFinanceLoading(true);
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.runFinanceSummary !== 'function') throw new Error('unavailable');
      /* ⭐ #744: the finance API takes the same {preset} as reports:ceo:run,
         so one period switch drives both and they cannot disagree. */
      const res = await api.runFinanceSummary(periodRequest);
      if (res && res.error) throw new Error(res.message || 'failed');
      setFinance(res);
      setFinanceFailed(false);
    } catch (e) {
      // ⚠ Told it FAILED. Never left holding stale or null data it would render
      // as zero revenue.
      setFinance(null);
      setFinanceFailed(true);
    } finally {
      setFinanceLoading(false);
    }
  }, [canView, canFinance, periodRequest]);

  /*
   * ⚠ Takes the SAME {preset} the other two take, so one period switch drives
   * all three and the affiliate figures cannot show a different window from
   * the tiles above them.
   */
  const loadAffiliate = useCallback(async () => {
    if (!canView) return;
    setAffiliateLoading(true);
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.runAffiliateSummary !== 'function') throw new Error('unavailable');
      const res = await api.runAffiliateSummary(periodRequest);
      if (res && res.error) throw new Error(res.message || 'failed');
      setAffiliate(res);
      setAffiliateFailed(false);
    } catch (e) {
      /* ⛔ Told it FAILED. Never left holding null, which the panel would
         otherwise have to render as a programme with no partners. */
      setAffiliate(null);
      setAffiliateFailed(true);
    } finally {
      setAffiliateLoading(false);
    }
  }, [canView, periodRequest]);

  useEffect(() => { loadAffiliate(); }, [loadAffiliate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAttention(); }, [loadAttention]);
  useEffect(() => { loadFinance(); }, [loadFinance]);
  /*
   * Per-rep figures for the Overview's team panel — Card P145.
   *
   * ⚠ A SEPARATE BUILDER, DELIBERATELY. Won $ and conversion come from
   * reports:reps:performance:run; overdue follow-ups come from the attention
   * rules. The Overview composes the two client-side rather than adding a
   * third server query — routes/_ceo_dashboard.js is W3's file this sprint,
   * and a fourth per-rep aggregate would be a fourth definition of "a rep".
   *
   * ⛔ A failure sets repsFailed; it never leaves `reps` as [] , which the
   * chart would draw as "every rep has zero overdue work".
   */
  const loadReps = useCallback(async () => {
    if (!canView) return;
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.runRepPerformance !== 'function') throw new Error('unavailable');
      // ⚠ `from` is REQUIRED by the channel (routes/reports.js). A rolling
      // 90-day window until W3's period parameter lands, at which point this
      // takes the selected range instead.
      const from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const res = await api.runRepPerformance({ from, to: null });
      if (res && res.error) throw new Error(res.message || 'failed');
      setReps((res && (res.reps || res.rows)) || []);

      /*
       * ⛔⛔ THE ROSTER IS A SECOND CALL, NOT A SECOND READING OF THE FIRST
       * (Card P187). Performance is a COHORT report: contacts created in the
       * window, grouped by owner. A rep with overdue work and no new contacts
       * has no row in it at all — measured 30 Oct, that hid ~1,128 overdue
       * tasks, about 76% of every overdue task in the system.
       *
       * ⚠ FAILS SOFT, DELIBERATELY. If the roster call fails the panel still
       * renders from performance rather than going blank: fewer rows is a
       * degraded panel, no panel is a dead one. The failure is recorded so the
       * two states stay distinguishable.
       */
      if (typeof api.runRepOverdue === 'function') {
        try {
          const od = await api.runRepOverdue();
          setRepOverdue((od && od.rows) || []);
        } catch (e) { setRepOverdue(null); }
      } else {
        setRepOverdue(null);
      }
      setRepsFailed(false);
    } catch (e) {
      setReps(null);
      setRepsFailed(true);
    }
  }, [canView]);

  useEffect(() => { loadReps(); }, [loadReps]);

  /*
   * ⭐⭐ "Where the open pipeline sits" — Card P152.
   *
   * ⚠ #732 does NOT ship a stage breakdown, and asking W3 for one would be a
   * third query over a figure that already has a grouping endpoint. #722's
   * resolver groups any drillable metric by stage, with counts and dollars,
   * behind the same analytics:drilldown permission the panel's links use.
   * So the panel is the summary the drill-down already computes, rather than
   * a parallel definition of "stage" that could disagree with it.
   *
   * ⛔ Gated on canDrill: without that permission the panel has no data it is
   * allowed to see, and says so rather than silently rendering empty.
   */
  const loadStages = useCallback(async () => {
    if (!canView || !canDrill) return;
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.invoke !== 'function') throw new Error('unavailable');
      const res = await api.invoke('analytics:drilldown:summary', {
        key: 'ceo.open_pipeline_value', dimension: 'stage',
      });
      if (res && res.error) throw new Error(res.message || 'failed');
      setStages((res && res.groups) || []);
      setStagesFailed(false);
    } catch (e) {
      setStages(null);
      setStagesFailed(true);
    }
  }, [canView, canDrill]);

  useEffect(() => { loadStages(); }, [loadStages]);

  /*
   * ⚠ Escape closes the drill modal. Bound only while one is open, so this
   * never competes with the dialogs other pages put on screen.
   */
  useEffect(() => {
    if (!drilldown) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrilldown(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drilldown]);




  /*
   * Row → contact, via the established one-shot sessionStorage hand-off that
   * Patients.js:918 already uses, then navigate to the CRM.
   *
   * ⚠ Deliberately NOT rendering contact detail inside the drill-down panel. The
   * contact drawer is what CARRIES THE PHI AUDIT for a contact read; showing the
   * same information here would disclose PHI through a path with no audit of its
   * own. Reusing the drawer means the audit comes for free and cannot drift from
   * the rest of the CRM.
   */
  /*
   * ⛔⛔ P179 item 2 — OPENING A RECORD USED TO END THE JOURNEY.
   *
   * Carlos: CEO Dashboard → Active pipeline → Qualified – Interested → a row →
   * the patient record, and then no way back to that pop-up. This navigated to
   * the CRM and dropped the modal, its frame stack, its sort and its filters on
   * the floor — getting back meant redoing all four steps by hand.
   *
   * ⭐ THE RECORD IS NOW A FRAME ON THE STACK THAT ALREADY EXISTS, not a
   * destination. Chosen over "navigate away and restore from the URL" because
   * the stack, its Back button and its breadcrumb are already built and already
   * tested (P167): restoring state from a URL would be a SECOND representation
   * of where the user is, and the two would drift — the failure this codebase
   * repeats. Nothing is serialised, so nothing can be restored wrongly.
   *
   * ⚠ ONE ENTRY POINT. Every drill-down receives this same `openContact`, so
   * the fix reaches the opportunity, finance, rep and exception lists at once
   * rather than four times.
   */
  const openContact = useCallback((contactId, row) => {
    if (contactId == null) return;
    pushFrame({
      kind: 'contact',
      contactId,
      /*
       * ⚠ A GENERIC TITLE, NEVER THE PATIENT'S NAME. The breadcrumb is read by
       * anyone looking at the screen and the modal title is the one string that
       * persists while frames change; the name is already on the record itself,
       * where the PHI audit covers it.
       */
      title: 'Record',
      rowId: row && row.id != null ? row.id : null,
    });
  }, [pushFrame]);

  /*
   * ⛔ `navigateToContact` WAS REMOVED BY CARD P186. It stashed a contact id in
   * sessionStorage and called onNavigate('crm') — the pre-frame-stack hand-off
   * that LEFT the dashboard. Its own comment claimed it was "KEPT for the paths
   * that genuinely LEAVE the dashboard", but a grep found NO CALLER: every
   * exception that navigates goes through `openAttentionItem`, which has its
   * own sessionStorage key.
   *
   * ⚠ A granted capability with no caller is not harmless here. It is the
   * mechanism behind the very bug this card was raised for, sitting one
   * autocomplete away from being wired back into a row handler — and a reader
   * comparing the two paths would reasonably assume it was still in use.
   */

  /*
   * One click to a filtered view. The item carries its own { view, filter }; this
   * hands it to the host page through the same one-shot sessionStorage channel
   * Patients.js:918 and openContact above already use, then navigates.
   *
   * ⚠ The filter is carried, NOT applied here. Applying it client-side would mean
   * the filtered list and the count on the panel were computed by two different
   * pieces of code — the drift that P140 PR B exists to remove.
   */
  /*
   * P160 item 2 — an exception opens EXACTLY the records it counts.
   *
   * ⛔⛔ It used to hand the item's `link` to sessionStorage and navigate to
   * CRM, which landed on My Day, unfiltered, with no mention of the exception.
   * A screen full of records that are not the ones counted is worse than a dead
   * button, because nothing on it says so.
   *
   * ⚠ A rule with NO mapping falls through to the old navigation rather than
   * opening something adjacent — being sent to the CRM is at least honest about
   * being a different screen, where a wrong drill-down is not.
   */
  const openAttentionItem = useCallback((item) => {
    if (!item || !item.link) return;
    try {
      sessionStorage.setItem('cowork.ceo.attentionFilter', JSON.stringify(item.link));
    } catch (e) { /* sessionStorage may be unavailable; the navigate below still helps */ }
    if (typeof onNavigate === 'function') onNavigate('crm');
  }, [onNavigate]);

  const openException = useCallback((item) => {
    if (!item || !item.ruleKey) return;
    const t = targetFor(item.ruleKey);
    const f = (item.link && item.link.filter) || {};

    /*
     * ⛔⛔ AN UNKNOWN RULE KEY OPENS NOTHING. It cannot happen while
     * tests/unit/attentionTargets.test.js passes — every rule key is required
     * to appear in the table — so reaching here means the table and the server
     * have drifted, and the honest response to "I do not know where this goes"
     * is a dead button, not a guess. The old silent fallback to CRM is what
     * this card removed.
     */
    if (!t) return;

    if (t.navigate) { openAttentionItem(item); return; }

    const filters = {};
    for (const k of (t.filtersFrom || [])) filters[k] = f[k];

    setException({
      key: t.metric,
      title: item.title,
      columns: null,
      dimension: t.dimension || null,
      group: t.groupBy
        ? { dimension: t.groupBy, key: f[t.groupFrom] == null ? null : f[t.groupFrom] }
        : null,
      filters: (t.filtersFrom || []).length ? filters : null,
    });
  }, [openAttentionItem]);

  /*
   * One frame of the drill-down stack. ⚠ Extracted from an inline IIFE so the
   * modal can render EVERY frame (hiding all but the top) — see the note at the
   * call site. Takes the frame rather than reading `stack` so it cannot
   * accidentally render the top one for a lower index.
   */
  const renderFrame = useCallback((top) => {
    if (!top) return null;
    if (top.kind === 'contact') {
      /*
       * ⭐ The record, INSIDE the modal. The frame below it — the row
       * list with its sort, its search and its group — is untouched, so
       * Back returns to exactly the view the record was opened from
       * rather than to a rebuilt approximation of it.
       */
      return (
        <div data-testid="drilldown-contact" style={{ padding: 0 }}>
          <ContactDetailView
            contactId={top.contactId}
            onBack={popFrame}
            allUsers={[]}
            canUpdate={false}
            user={user}
          />
        </div>
      );
    }
    if (top.kind === 'rep') {
      return (
        <RepReport
          rep={top.rep}
          period={periodLabel}
          filters={periodFilters}
          /*
           * ⚠⚠ THE CHILD'S OWN CLOSE IS WIRED TO THE SAME STACK.
           * Dropping onClose left those buttons rendered and INERT —
           * the exact defect P153 spent a card removing from this
           * screen. A control that exists must do something; if the
           * shell owned dismissal alone, the child's chrome would have
           * to go, and that is a bigger change than this card needs.
           */
          onClose={closeStack}
          onOpenContact={openContact}
          canExport={canExport}
        />
      );
    }
    if (top.kind === 'ai') {
      /*
       * P182 PR2 — the CEO pipeline AI report.
       * ⚠ Rebased onto P179's frame stack (#756/#757): every frame stays
       * MOUNTED and only the top one is visible, so this panel keeps its
       * question text, its generated report and its figures toggle when the
       * user opens a record from it and presses Back. The pre-rebase version
       * of this file rendered only the top frame and would have thrown that
       * state away — main's behaviour is the better one and is kept.
       */
      return (
        <AiPipelineReport
          /* ⭐ P185 item 4 — W1's report follows the window. `periodLabel` is
             the range in words for its heading; `filters` carries the actual
             from/to, which it already forwards to its channel. One getter
             (`currentRange`) is exposed for anything that needs more. */
          period={periodLabel}
          range={currentRange}
          filters={aiReportFilters}
          /* ⭐ P224 — the SAME grant every other export on this screen uses. */
          canExport={canExport}
          /* The child's own Close drives the shared stack — P153. */
          onClose={closeStack}
        />
      );
    }
    if (top.kind === 'finance') {
      return (
        <FinanceDrilldown
          metricKey={top.key}
          title={top.title}
          onClose={closeStack}
          onOpenContact={openContact}
          canExport={canExport}
        />
      );
    }
    return (
      <>
        {/*
          * ⭐ P196 item 4 — THE SHAPE BEFORE THE ROWS. A compact funnel sits
          * above the table so Carlos sees where the value sits before reading
          * a list. ⚠ Only on a stage-grouped frame: a rep or finance
          * drill-down has no funnel, and a chart that does not describe the
          * rows beneath it is decoration.
          */}
        {charts && chartsState === 'ok'
          && top.group && top.group.dimension === 'stage' ? (
          <div style={{ marginBottom: 10 }} data-testid="drilldown-chart">
            <SalesFunnel
              compact
              height={150}
              funnel={charts.funnel}
              unmappedStages={charts.unmappedStages}
              winRate={charts.winRate}
              terminalBuckets={charts.terminalBuckets}
            />
          </div>
        ) : null}
      <MetricExplorer
        metricKey={top.key}
        title={top.title}
        onClose={closeStack}
        onOpenContact={openContact}
        canExport={canExport}
        channels={top.channels === 'rep' ? REP_CHANNELS
                  : top.channels === 'finance' ? FINANCE_CHANNELS : undefined}
        columns={top.columns}
        initialGroup={top.group}
        filters={top.filters || periodFilters}
        /* ⭐ Opening rows from a group PUSHES a frame rather than
           replacing one — that is what gives Back something to walk. */
        onOpenGroup={(g) => pushFrame({
          kind: 'metric', key: top.key, title: g && g.label ? g.label : 'Rows',
          /*
           * ⛔⛔ THE WHOLE GROUP, not `g.dimension`. This pushed the
           * dimension STRING into a slot that MetricExplorer reads as a
           * group object — so the frame rendered the SUMMARY again
           * instead of the row list. It was invisible until P179 item 2
           * gave anyone a reason to come BACK to this frame: before, you
           * only ever went forward through it, and the frame it pushed
           * was never re-rendered.
           */
          group: g || null, filters: top.filters, channels: top.channels,
        })}
      />
      </>
    );
  }, [popFrame, pushFrame, closeStack, openContact, canExport, periodFilters, user,
      charts, chartsState]);

  /*
   * Card P254 — the goal tracker payload.
   *
   * ⚠ ITS DEPENDENCY LIST HAS NO `periodFilters` IN IT, AND THAT IS THE
   * POINT. Every other fetch on this page re-runs when Carlos changes the
   * period control; this one must not, because the question it answers is
   * about the CURRENT month. Adding periodFilters here would make the tracker
   * silently report last quarter's goal while still being headed "November
   * 2026 Sales Goal".
   *
   * `goalNonce` is the ONLY thing that re-runs it: it is bumped after a
   * successful target write, so the tracker reflects the new goal without a
   * page reload.
   */
  React.useEffect(() => {
    let cancelled = false;
    const api = typeof window !== 'undefined' ? window.zignagenixAPI : null;
    if (!api || typeof api.getCeoGoal !== 'function') { setGoalState('untracked'); return undefined; }
    setGoalState('loading');
    api.getCeoGoal()
      .then((r) => {
        if (cancelled) return;
        /* ⚠ A failed load is 'failed', never an empty tracker. An empty goal
           card reads as "no goal set", which would send Carlos to set a
           target he has already set. */
        if (!r || r.error) { setGoal(null); setGoalState('failed'); return; }
        setGoal(r); setGoalState('ok');
      })
      .catch(() => { if (!cancelled) { setGoal(null); setGoalState('failed'); } });
    return () => { cancelled = true; };
  }, [canView, goalNonce]);

  /*
   * ⚠ RE-READS RATHER THAN PATCHING LOCAL STATE. After a write, the pace,
   * the remaining figure and the attained percentage all change together;
   * recomputing any of them in the browser is how the screen starts
   * disagreeing with the server that produced the rest of it.
   *
   * ⚠ The error is RE-THROWN so the editor can show it. Swallowing it here
   * would close the form over a write that never landed.
   */
  const saveGoalTarget = React.useCallback(async (month, targetCents) => {
    const api = typeof window !== 'undefined' ? window.zignagenixAPI : null;
    if (!api || typeof api.setCeoGoalTarget !== 'function') {
      throw new Error('Setting a target is not available in this build.');
    }
    const res = await api.setCeoGoalTarget(month, targetCents);
    if (!res || res.error) {
      throw new Error((res && res.error) || 'The target could not be saved.');
    }
    setGoalNonce((n) => n + 1);
    return res;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const api = typeof window !== 'undefined' ? window.zignagenixAPI : null;
    if (!api || typeof api.getCeoCharts !== 'function') { setChartsState('untracked'); return undefined; }
    setChartsState('loading');
    api.getCeoCharts({ ...periodFilters, pipelineId: chartPipeline || null })
      .then((r) => {
        if (cancelled) return;
        /*
         * ⚠ A failed load is 'failed', never an empty chart. An empty chart
         * reads as "the business did nothing", which is a different and much
         * worse statement than "this did not load".
         */
        if (!r || r.error) { setCharts(null); setChartsState('failed'); return; }
        setCharts(r); setChartsState('ok');
      })
      .catch(() => { if (!cancelled) { setCharts(null); setChartsState('failed'); } });
    return () => { cancelled = true; };
  }, [periodFilters, chartPipeline]);

  if (!canView) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6C757D' }}>
        <ShieldAlert size={28} color="#ADB5BD" />
        <div style={{ marginTop: 10, fontWeight: 600 }}>You don&rsquo;t have access to the CEO Dashboard.</div>
        <div style={{ fontSize: 13, color: '#ADB5BD', marginTop: 4 }}>
          This report requires the management reporting permission.
        </div>
      </div>
    );
  }

  return (
    /*
     * ⭐ Card P198/P199 — the ONE-LINE scope for the soft-UI look. Every rule
     * and every design token in ceoSoftUi.css is nested under this class, so
     * nothing outside the CEO dashboard can inherit any of it. A test asserts
     * the class exists here and on no other page.
     */
    <div className="ceo-soft" style={{ padding: 20 }}>
      {error && (
        <div style={{ padding: 12, borderRadius: 10, background: '#FDECEA', color: '#7B241C', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}
      {!data && loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#ADB5BD' }}>Loading CEO dashboard…</div>
      )}
      {/*
        * Above the tiles, and rendered whether or not the aggregate loaded — the
        * two are independent, so a failed dashboard must not hide live risks and
        * a failed panel must not hide live numbers.
        */}
      {view === 'detail' && <NeedsAttention
        data={attention}
        failed={attentionFailed}
        loading={attentionLoading}
        onOpen={openAttentionItem}
      />}
      {view === 'detail' && <FinancePanel
        data={finance}
        failed={financeFailed}
        loading={financeLoading}
        canView={canFinance}
        onOpenMetric={canFinance ? ((key) => setFinanceMetric(key)) : undefined}
      />}
      {/*
        * P150. The drill-down renders beside the card it opened from, in the
        * DETAIL view only — the Overview is a summary screen and a 25-row table
        * underneath it would defeat the point of having one.
        */}
      {/*
        * ⚠ RENDERED IN BOTH VIEWS. The Overview's Receivables figures open the
        * same drill-down, and bouncing the user to "All figures" to show it
        * would lose the screen they were reading. The panel appears beneath
        * whichever view opened it.
        */}
      {/*
        * P160 item 6 — ONE anchored region for every drill-down, so whichever
        * opens is scrolled to and focused. tabIndex -1 makes it focusable
        * without putting it in the tab order.
        */}
      {/*
        * ⛔⛔ CARD P229 — THE AI REPORT DOES NOT CLOSE ON A BACKDROP CLICK.
        * It takes about a minute of the model's time to write; losing it to a
        * stray click (and Print and Download PDF with it) is not a trade
        * worth making. Every OTHER frame keeps the click-outside-to-close it
        * has always had — the prop is per-render, and the top frame decides.
        */}
      <DrilldownModal stack={stack} onBack={popFrame} onClose={closeStack}
                      sessionNonce={sessionNonce}
                      closeOnBackdrop={!(stack.length && stack[stack.length - 1].kind === 'ai')}
                      /* ⛔ Esc only while a report is actually being written. */
                      closeOnEsc={!(stack.length && stack[stack.length - 1].kind === 'ai'
                                    && aiJobRunning)}>
        {/*
          * ⛔⛔ EVERY FRAME STAYS MOUNTED; only the top one is visible.
          *
          * Rendering just the top frame destroyed the ones beneath it, and with
          * them everything that lives in their component state — which group is
          * open, the sort, the search box, the scroll position. Opening a record
          * and pressing Back then returned to a REBUILT panel: the row list came
          * back as a summary, because MetricExplorer's group selection is its own
          * state and had been thrown away. P179 item 2 asks for the way back to
          * restore "the same modal, frame stack, sort and filters"; keeping the
          * frames alive IS that, with nothing serialised and nothing to restore
          * wrongly.
          *
          * ⚠ `hidden`, not unmounting and not `display:none` set by hand — it
          * keeps the frame out of the accessibility tree as well as out of
          * sight, so a screen reader does not read two panels at once.
          */}
        {stack.map((frame, idx) => (
          <div key={frame.id != null ? frame.id : idx}
               hidden={idx !== stack.length - 1}
               data-testid={`drilldown-frame-${idx}`}>
            {renderFrame(frame)}
          </div>
        ))}
      </DrilldownModal>

      <div style={{ display: 'flex', gap: 6, margin: '0 0 12px' }} role="group" aria-label="Dashboard view">
        {[['overview', 'Overview'], ['detail', 'All figures']].map(([k, label]) => (
          <button
            key={k} type="button" onClick={() => setView(k)} aria-pressed={view === k}
            data-testid={`view-${k}`}
            style={{
              padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${view === k ? '#1D4ED8' : '#E3E7EE'}`,
              background: view === k ? '#1D4ED8' : '#FFFFFF',
              color: view === k ? '#FFFFFF' : '#4B5563',
            }}
          >{label}</button>
        ))}
      </div>

      {view === 'overview' && (
        <div ref={overviewRef}><CeoOverview
          /* P182 PR2 — the AI report. The whole page is already behind
             reports:management:read (MGMT_PERM above) and the server gates
             ai:reports:generate:ceo on the same string, so anyone who can see
             this header can run it. The server remains the authority. */
          onOpenAiReport={openAiReport}
          aiReportBusy={aiJobRunning}
          /*
           * ⭐ Card P229 — the way back to a report that has been closed. It
           * reads the P219 job store, so there is no new state here and
           * nothing to keep in sync: the brief was always still in memory.
           */
          lastReportChip={(
            <LastReportChip
              canExport={canExport}
              onView={openAiReport}
              /* ⛔ Print and Download need the report RENDERED — they
                 rasterise the charts the reader is looking at. So both
                 reopen the dialog first and let it do the work; a chip that
                 printed from nothing would produce a page of text with
                 seven blank spaces where the charts belong. */
              onPrint={() => { openAiReport(); setTimeout(() => window.print(), 400); }}
              onDownload={openAiReport}
            />
          )}
          /* P196 — the graphics. Same period as the tiles, by construction. */
          charts={charts}
          chartsState={chartsState}
          /* Card P254 — the goal tracker, above everything else. */
          goal={goal} goalState={goalState}
          canSetTarget={canSetTarget} onSaveTarget={saveGoalTarget}
          chartPipeline={chartPipeline}
          onChartPipelineChange={setChartPipeline}
          onDrillFunnel={(bucket) => pushFrame({
            kind: 'metric', key: 'pipeline.open', title: bucket || 'Stage',
            group: { dimension: 'stage', label: bucket || 'Stage' }, filters: periodFilters,
          })}
          onDrillMovement={(key, label) => pushFrame({
            kind: 'metric', key: 'pipeline.open', title: label || 'Deals',
            group: { dimension: 'stage', label: label || 'Deals' }, filters: periodFilters,
          })}
          onOpenRep={canDrill ? ((rep) => setRepOpen(rep)) : null}
          data={data}
          attention={attention}
          attentionState={attentionFailed ? 'failed' : attentionLoading ? 'loading' : 'ok'}
          finance={canFinance ? finance : null}
          financeState={!canFinance ? 'untracked' : financeFailed ? 'failed' : financeLoading ? 'loading' : 'ok'}
          affiliate={affiliate}
          affiliateState={affiliateFailed ? 'failed' : affiliateLoading ? 'loading' : 'ok'}
          stages={stages}
          stagesState={!canDrill ? 'untracked' : stagesFailed ? 'failed' : stages ? 'ok' : 'loading'}
          reps={reps}
          repOverdue={repOverdue}
          repsState={repsFailed ? 'failed' : reps ? 'ok' : 'loading'}
          period={period}
          onPeriodChange={setPeriod}
          /* P185 item 1 — the custom window, beside the preset pills. */
          customRange={custom}
          onCustomRange={applyCustom}
          asOf={(data && data.asOf) || null}
          downloadMenu={(
            <DownloadMenu
              getModelArgs={getModelArgs}
              rootRef={overviewRef}
              onAudit={auditExport}
              canFinance={canFinance}
              disabled={!data}
            />
          )}
          /*
           * ⛔⛔ KEEP THE TITLE LOOKUP. #742 restored `title: key` here, which
           * is the exact P160 item 5 defect — a panel headed
           * `ceo.open_pipeline_value` on the CEO's screen. Neither branch was
           * wrong alone: #742 branched before item 5 landed. An unknown key
           * opens NOTHING, because a missing panel is a visible bug and a
           * key-as-title is an invisible one.
           */
          onDrill={canDrill ? ((key) => {
            const title = metricTitle(key);
            if (title) setDrilldown({ key, title });
          }) : () => {}}
          // P160 item 2 — openException maps a rule to the metric returning
          // exactly its records, falling back to openAttentionItem when a rule
          // has no mapping.
          onOpenAttentionItem={openException}
          onShowAllAttention={() => setView('detail')}
          // ⛔ P160 item 1 — this opened the GENERIC Reports page. It now opens
          // the team grouped by rep, where each group is one rep's records.
          onOpenTeamReport={canDrill
            ? (() => setException({ key: 'rep.overdue_tasks', title: 'Overdue follow-ups by rep', group: null, columns: null, filters: null }))
            : undefined}
          onOpenRevenueReport={canFinance ? (() => setFinanceMetric('finance.collected')) : (() => setView('detail'))}
          onDrillFinance={canFinance ? ((key) => setFinanceMetric(key)) : null}
          canExport={canExport}
          onExport={() => setDrilldown({ key: 'ceo.open_pipeline_value', title: 'Open pipeline value' })}
        /></div>
      )}

      {view === 'detail' && data && (        <CeoDashboard
          data={data}
          onRefresh={load}
          loading={loading}
          onDrilldown={canDrill ? setDrilldown : undefined}
        />
      )}
      {/*
        * ⛔⛔ A MODAL, NOT A PANEL AT THE END OF THE PAGE — v2.1.12.
        *
        * The Overview is a full-height screen, so a drill panel appended
        * after it opened BELOW THE FOLD: clicking "View" on row four of
        * Receivables appeared to do nothing at all, because the thing it
        * opened was a screen-height away.
        *
        * ⚠ Scrolling it into view was the smaller change and the worse one —
        * it leaves the answer's position depending on where the question was
        * asked, and on a short viewport the panel still opens mostly
        * offscreen. An overlay is in the same place every time.
        *
        * Dismissible by backdrop click and by Escape, because a modal that
        * can only be closed by finding its button is its own trap.
        */}
      {data && canDrill && drilldown && (
        <div
          data-testid="drilldown-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setDrilldown(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '48px 16px', overflowY: 'auto',
          }}
        >
          <div role="dialog" aria-modal="true" aria-label={drilldown.title || 'Figure detail'}
               style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1040,
                        boxShadow: '0 20px 60px rgba(0,0,0,.3)', padding: 8 }}>
            <MetricExplorer
              metricKey={drilldown.key}
              title={drilldown.title}
              onClose={() => setDrilldown(null)}
              onOpenContact={openContact}
              canExport={canExport}
            />
          </div>
        </div>
      )}
    </div>
  );
}
